import { Component, EventEmitter, Input, Output } from '@angular/core';
import { EventDoc, EventMediaItem } from '../../models/event.model';
import {
  formatEventTimeRange,
  hasPromotion,
  heroMedia,
  locationLabel,
  primaryLocation,
} from '../event-utils';

/**
 * The featured-carousel card — iOS `EventCardView`.
 *
 * A full-bleed image with the notched silhouette, a scrim, and the title block sitting on the lower
 * third. Sized entirely by its parent (the carousel sets width and height); the card fills it.
 *
 * The calendar button stops propagation so it never opens the details screen by accident — on iOS
 * the two are separate tap targets by construction, but here the whole card is one click handler.
 */
@Component({
    selector: 'app-event-card',
    templateUrl: './event-card.component.html',
    styleUrls: ['./event-card.component.scss'],
    host: { class: 'events-scope' },
    standalone: false
})
export class EventCardComponent {
  @Input() event!: EventDoc;
  /** iOS passes `false` from both list sections; the venue chip is for cross-venue surfaces. */
  @Input() showBusinessInfo = false;

  /**
   * Per-instance id for the notch `clipPath`. The carousel renders many of these, and a document
   * with N copies of `id="evCardNotch"` is invalid HTML — every card would resolve to whichever
   * copy the browser saw first, so removing one card could restyle the others.
   */
  private static nextNotchId = 0;
  readonly notchId = `evCardNotch-${EventCardComponent.nextNotchId++}`;
  readonly notchClipPath = `url(#${this.notchId})`;

  /** Emits the event's id — the parent owns navigation and analytics `entry_point`. */
  @Output() openEvent = new EventEmitter<string>();
  @Output() addToCalendar = new EventEmitter<EventDoc>();

  get media(): EventMediaItem | null {
    return heroMedia(this.event);
  }

  get hasPromotion(): boolean {
    return hasPromotion(this.event);
  }

  get place(): string {
    return locationLabel(primaryLocation(this.event));
  }

  get address(): string {
    return (primaryLocation(this.event)?.address ?? '').trim();
  }

  get time(): string {
    return formatEventTimeRange(this.event);
  }

  onOpen(): void {
    if (this.event?.id) {
      this.openEvent.emit(this.event.id);
    }
  }

  onCalendar(mouseEvent: Event): void {
    mouseEvent.stopPropagation();
    this.addToCalendar.emit(this.event);
  }
}
